import AreaRegion from "@mapmycustomers/shared/types/territory/AreaRegion";
import AreaType from "@mapmycustomers/shared/types/territory/AreaType";
import Tile from "@mapmycustomers/shared/types/territory/Tile";

import type { BoundaryCache } from "./boundaryCache";
import type { DataLoader } from "./dataLoader";

const BATCH_COUNT = 5;
const BATCH_MAX_SIZE = 500;

export type LoadListener = (
  regions: AreaRegion[],
  areaType: AreaType,
  zoomLevel: number,
  tiles?: Tile[]
) => void;

class CachingDataLoader {
  private _loader: DataLoader;
  private _cache: BoundaryCache;
  private _lastRequest?: { areaType: AreaType; tiles?: Tile[]; zoomLevel: number };
  private _loadListener?: LoadListener;

  constructor(loader: DataLoader, cache: BoundaryCache) {
    this._loader = loader;
    this._cache = cache;
  }

  set loadListener(listener: LoadListener) {
    this._loadListener = listener;
  }

  async loadRegionsForZoomLevel(
    areaType: AreaType,
    zoomLevel: number
  ): Promise<AreaRegion[] | undefined> {
    this._lastRequest = { areaType, zoomLevel };

    if (!this._cache.hasRegionsForZoomLevel(areaType, zoomLevel)) {
      const regions = await this._loader.loadRegionsForZoomLevel(areaType, zoomLevel);
      if (!regions) {
        return [];
      }
      this._cache.addRegionsForZoomLevel(areaType, zoomLevel, regions);
    }

    const regions = this._cache.getRegions(areaType, zoomLevel);
    // console.log("regions from cache for ", areaType, zoomLevel, regions.length);
    this._loadListener?.(regions, areaType, zoomLevel);
    return regions;
  }

  async loadDataForTiles(areaType: AreaType, zoomLevel: number, tiles: Tile[]): Promise<any> {
    this._lastRequest = { areaType, tiles, zoomLevel };

    const tilesToLoad = new Set(tiles);
    tiles.forEach((tile) => {
      if (this._cache.hasTileInformation(areaType, tile)) {
        tilesToLoad.delete(tile);
      }
    });

    const data = await this._loader.loadDataForTiles(areaType, zoomLevel, Array.from(tilesToLoad));
    data.forEach((item) => {
      this._cache.addTilesInformation(areaType, item.tile, item.regionIds);
    });

    const regionIdsToLoad = tiles.flatMap((tile) => {
      return this._cache
        .getTileInformation(areaType, tile)
        .filter((regionId) => !this._cache.hasRegionForZoomLevel(areaType, zoomLevel, regionId));
    });

    if (regionIdsToLoad.length) {
      let batchSize = Math.min(Math.ceil(regionIdsToLoad.length / BATCH_COUNT), BATCH_MAX_SIZE);
      if (batchSize < 2) {
        // if number of records to load is too small, don't bother batching them, load all in one request
        batchSize = regionIdsToLoad.length;
      }
      while (regionIdsToLoad.length) {
        // loading in batches allows user to see some result sooner
        const batch = regionIdsToLoad.splice(0, batchSize);
        this._loader.loadTileRegions(areaType, zoomLevel, batch).then((regions) => {
          regions.forEach((region) => {
            this._cache.addRegionForZoomLevel(areaType, zoomLevel, region);
          });
          this._loadListener?.(
            this._cache.getRegions(areaType, zoomLevel, tiles),
            areaType,
            zoomLevel,
            tiles
          );
        });
      }
    }

    // since there might be some or all data cached, let's call listener
    this._loadListener?.(
      this._cache.getRegions(areaType, zoomLevel, tiles),
      areaType,
      zoomLevel,
      tiles
    );
  }
}

export default CachingDataLoader;
